import React, { useRef, useState, useEffect } from 'react';
import { Modal, QRCode, Button, Segmented, Space, Typography, Slider, message } from 'antd';
import useParticipantBadges from '../../hooks/participants/useParticipantBadges';
import axios from '../../config/axiosInstance';
import { CloudDownloadOutlined, PoweroffOutlined } from '@ant-design/icons';

const { Text } = Typography;

const ModalParticipantQR = ({ open, onClose, participant, value, host }) => {
    const [renderType, setRenderType] = useState('canvas'); // canvas = PNG, svg = SVG
    const [padding, setPadding] = useState(10);
    const [disabling, setDisabling] = useState(false);
    const qrRef = useRef(null);

    const participantId = participant?.key || null;
    const { badges, fetching, fetchBadges } = useParticipantBadges(participantId, host);
    const activeBadge = badges.find(b => !b.disabled);

    useEffect(() => {
        if (open && participantId && host) {
            fetchBadges();
        }
    }, [open, participantId, host]);

    const getToken = async () => {
        const res = await axios.get(`/regasus/token?host=${host}`);
        return res.data.access_token;
    };

    const handleDisableBadge = async () => {
        if (!activeBadge) return;

        try {
            setDisabling(true);
            const token = await getToken();
            await axios.put('/regasus/disable-badge', {
                token,
                host,
                badgeId: activeBadge.id,
            });
            message.success(`Badge ${activeBadge.badgeID} disabled`);
            fetchBadges();
        } catch (err) {
            console.error(err);
            message.error('Failed to disable badge');
        } finally {
            setDisabling(false);
        }
    };

    const doDownload = (url, fileName) => {
        const a = document.createElement('a');
        a.download = fileName;
        a.href = url;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const vigenereCode = value?.split(';')[0] || 'ParticipantQR';

    const downloadCanvasQRCode = () => {
        const canvas = qrRef.current?.querySelector('canvas');
        if (canvas) {
            const url = canvas.toDataURL('image/png');
            doDownload(url, `${vigenereCode}.png`);
        } else {
            console.warn('❌ Canvas not found.');
        }
    };

    const downloadSvgQRCode = () => {
        const svg = qrRef.current?.querySelector('svg');
        if (svg) {
            const svgData = new XMLSerializer().serializeToString(svg);
            const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            doDownload(url, `${vigenereCode}.svg`);
        } else {
            console.warn('❌ SVG not found.');
        }
    };

    return (
        <Modal
            open={open}
            footer={null}
            onCancel={onClose}
            centered
            width={320}
            styles={{
                body: {
                    padding: 20,
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                },
                content: { padding: 0 },
            }}
        >
            {participant && (
                <h2 style={{ textAlign: 'center', marginTop: 8, marginBottom: 16 }}>
                    {participant.firstName} {participant.lastName}
                </h2>
            )}
            <Space ref={qrRef} direction="vertical" align="center" style={{ width: '100%' }}>
                <Segmented
                    options={[
                        { label: 'PNG', value: 'canvas' },
                        { label: 'SVG', value: 'svg' }
                    ]}
                    value={renderType}
                    onChange={setRenderType}
                />
                <QRCode
                    type={renderType}
                    errorLevel="H"
                    value={value}
                    size={220}
                    icon="/default_appicon.svg"
                    iconSize={60}
                    bgColor="#ffffff"
                    color="#000000"
                    style={{ padding }}
                />
                <Slider
                    min={5}
                    max={80}
                    step={1}
                    value={padding}
                    onChange={setPadding}
                    style={{ width: 200 }}
                />
                <div style={{ marginBottom: 12 }}>
                    <Text type="secondary">
                        Vigenere Code: <strong>{vigenereCode}</strong>
                    </Text>
                </div>
                <Space style={{ marginTop: 10 }} size="middle">
                    <Button
                        icon={<CloudDownloadOutlined />}
                        type="primary"
                        onClick={renderType === 'canvas' ? downloadCanvasQRCode : downloadSvgQRCode}
                        style={{ minWidth: 100, height: 60, borderRadius: 6 }}
                    />
                    {activeBadge && (
                        <Button
                            icon={<PoweroffOutlined />}
                            danger
                            type="primary"
                            loading={disabling}
                            onClick={handleDisableBadge}
                            style={{ minWidth: 100, height: 60, borderRadius: 6 }}
                        >
                            Deactivate Badge
                        </Button>
                    )}
                </Space>
            </Space>
        </Modal>
    );
};

export default ModalParticipantQR;